import React, { useEffect } from 'react'
import HeatmapVisualization from "@/pageTpls/Playground/pages/Common/HeatmapVisualization";
import {
  AccordionV2,
  ActionMenu,
  BarChart,
  colors2,
  makeArrayFromLength,
  SkeletonLoader,
  UI_BODY_BOLD_SM_DF
} from "oolib";
import styled from "styled-components";
import { useGetQueryData } from "../../../../../../../utils/react-query-hooks/general";
import { useAggrMetricsAcrossDimensions } from './useAggrMetricsAcrossDimensions';
import { extractAllBlocksFromTpl } from '@/utils/templating/extractAllBlocksFromTpl';

const StyledHeader = styled.div`
  width: 100%;
  padding: 1rem 1.6rem;
  text-align: left;
  background: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const getShortLabelOfThisBlockFromTpl = ({contentType, dimensions, allTpls,}) => {
    const tpl = allTpls.find(d => d.kp_content_type === contentType);
    if(!tpl) return undefined;
    //else
    const allBlocks = extractAllBlocksFromTpl({tpl});
    const thisBlock = allBlocks.find(bl => bl.valuePath === dimensions[0]);
    if(!thisBlock) return undefined;
    //else
    return thisBlock.props?.shortlabel || thisBlock.props?.label
  }

export const Component = ({
  contentType,
  dimensions,
  id,
  index,
  activeFilters = [],
  actions,
  setWidgetTitle
}) => {
  const allTpls = useGetQueryData(["allTpls"]);

  const {data, status, error} = useAggrMetricsAcrossDimensions({
    activeFilters,
    vizCardId: id,
    contentType: contentType,
    dimensions: dimensions,
    metrics: [{ "type": "count", "name": "count" }],
    findQuery: { "kp_published_status": "published" },
    queryOptions: { enabled: true },
  })

  useEffect(() => {
    if(status === 'success'){
      setWidgetTitle && setWidgetTitle(
        getShortLabelOfThisBlockFromTpl({ contentType, dimensions, allTpls })
      )
    }
  },[status, data])
  

  const genViz = () => {
    if (data.xValues && data.yValues) {
      return (
        <AccordionV2>
          <HeatmapVisualization data={data} />
        </AccordionV2>
      );
    }

    //else

    const getLengthOfLongestChildrenArray = (data) => {
      let longestSoFar = 0;
      for(const d of data){
        if(d.children.length > longestSoFar){
          longestSoFar = d.children.length
        }
      }
      return longestSoFar;
    }

    return (
      <div key={JSON.stringify(activeFilters)}>
        
        <div style={{ margin: `0 -6px` }}>
          {/*minus 6 because bar charts have that slight 6px margin left and right */}
          <BarChart
            data={data}
            valuePath={
              dimensions.length === 1 
              ? "count" 
              : makeArrayFromLength(getLengthOfLongestChildrenArray(data)).map((d,i) => `children.${i}.count`)
            }
            tooltipLabelsPath={dimensions.length > 1 && makeArrayFromLength(getLengthOfLongestChildrenArray(data)).map((d,i) => `children.${i}.display`)}
            labelPath="display"
            colorIdx={index % 4}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {status === "loading" ? (
        <div style={{display: 'flex', flexDirection: 'column', gap: '0.4rem', height: '100%'}}>
          <SkeletonLoader style={{  height: '20rem', borderRadius: '4px', flexGrow: 1}}/>
        </div>
      ) : status === "error" ? (
        <div style={{height: '100%', borderRadius: '4px', background: colors2.redBG}}>Error</div>
      ) : (
        status === "success" && genViz()
      )}
    </>
  );
};

export const optionDef = { display: "Bar Chart", value: "barChart", comp: 'DataVizWidgetGeneratorV2' };

export const defaultConfig = {props: {}} //undefined; // could be some default props in this format: { props: {... } }

export const metaConfig = [
  {
    comp: 'DropdownSingle',
    valuePath: 'props.contentType',
    isRequired: true,
    props: {
      optionsFn: {
        fn: 'getContentTypes'
      },
      label: 'Content Type',
      saveValueAsString: true
    }
  },
  // {
  //   comp: 'DropdownMulti',
  //   valuePath: 'props.dimensions',
  //   isRequired: true,
  //   propModifiers: [  // note: it's propModifiers, not propsModifiers
  //     {
  //       targetValuePath: 'props.contentType',
  //       propSideEffects: [
  //         {
  //           valuePath: 'optionsFn.args.0.contentType',
  //           value: '$setToTargetValue'  // This special value tells the modifier to use the target value
  //         }
  //       ]
  //     }
  //   ],
  //   props: {
  //     optionsFn: {
  //       fn: 'getValuePathsFromTpls',
  //       args: [{}]
  //     },
  //     label: 'Dimensions',
  //     saveValueAsString: true
  //   }
  // },
  {
    comp: 'DropdownMulti',
    valuePath: 'props.RAW_dimensions',
    isRequired: true,
    propModifiers: [  // note: it's propModifiers, not propsModifiers
      {
        targetValuePath: 'props.contentType',
        propSideEffects: [
          {
            valuePath: 'optionsFn.args.0.contentType',
            value: '$setToTargetValue'  // This special value tells the modifier to use the target value
          }
        ]
      }
    ],
    props: {
      optionsFn: {
        fn: 'getFieldsCompatibleWithAggrMetricsAcrossDimensions',
        args: [{}]
      },
      label: 'Test'
    }
  }
]

export const processRawConfigFn = _widgetConfig => {
  const contentType = _widgetConfig.props.contentType;
  const dimensions = [];
  _widgetConfig.props.RAW_dimensions.forEach(dim => {
    if(!dim.rollupType){ //means its a normal tag or option select field option
      dimensions.push(dim.value) // which is basically a valuePath to a tag / option select block.
    }else if(dim.rollupType === 'tagType'){
      dimensions.push({
        rollupType: dim.rollupType,
        path: dim.rollupPath
      }) 
    }else if(dim.rollupType === 'valuePathType'){
      dimensions.push({
        rollupType: dim.rollupType,
        path: [dim.tagTypeCollectionToRollup, dim.blockValuePath]
      }) 
    }
  })
  return ({
    ..._widgetConfig,
    props: {
      ..._widgetConfig.props,
      contentType,
      dimensions
    }
  })
}